import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Calendar,
  ChevronDown,
  FileBarChart2,
  FileSpreadsheet,
  Filter,
  Loader2,
  Search,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { SortableTh } from '@/shared/components/ui/sortable-th';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { cn } from '@/shared/lib/cn';
import { coincideBusqueda, ordenarFilas, prepararBusqueda, type Orden } from '@/shared/lib/tablaOrden';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { useIsAdminTier, useIsReadOnly, useProfile } from '@/shared/auth/SessionProvider';
import {
  downloadReportXlsx,
  getReportPreview,
  importarMovimientosHistorico,
  type ReportFilters,
  type ReportId,
  type ReportPreviewResponse,
  type ReportTotal,
} from './api';
import {
  CierresDeCajaBody,
  CierresDeCajaToolbar,
  useCierresFilters,
} from './CierresDeCajaReport';
import { ImportarReclasificacionDialog } from './ImportarReclasificacionDialog';
import {
  FacturacionBody,
  FacturacionFilter,
  FacturacionSearch,
  useFacturacionReport,
} from '@/features/facturacion/FacturacionReport';
import {
  EstadoFinancieroBody,
  EstadoFinancieroToolbar,
  useEstadoFinancieroFilters,
} from './EstadoFinancieroReport';
import { exportEstadoFinanciero } from './efExport';
import { GarantiasTable } from './GarantiasReport';
import { fechaArchivoHoy } from '@/shared/lib/xlsx';
import { currencyFromSp, formatMoney } from '@/shared/lib/money';

/**
 * Reportes — unified shell for 6 canned reports. Matches the PowerApps
 * Screen_Reportes layout: Exportar Excel button + report dropdown +
 * Filtrar popover in the toolbar, table body, and per-report totals
 * footer (Total ISAR / Total Retenciones style).
 *
 * Filters are held in the Filtrar popover (date range OR mm/yyyy
 * depending on the report) so the toolbar stays compact. Preview
 * re-fetches automatically when filters change and are valid.
 */

/** `none`: reporte sin filtros (p. ej. el catálogo vigente) — sin Filtrar. */
type FilterKind = 'date-range' | 'month' | 'none';

type ReportDef = {
  id: ReportId;
  label: string;
  description: string;
  filterKind: FilterKind;
  /** date-range: `desde` inicial si no es el 1° del mes (dd/mm/yyyy). Una
   *  función cuando depende de hoy (p. ej. el 1° de enero del año en curso). */
  desdeDefault?: string | (() => string);
};

// Order + labels mirror PowerApps Screen_Reportes dropdown exactly so the
// admin muscle memory carries over.
const REPORTS: ReportDef[] = [
  {
    id: 'retenciones',
    label: 'Retenciones',
    description: 'Retenciones aplicadas en el mes — base, monto retenido, tipo y concepto.',
    filterKind: 'month',
  },
  {
    id: 'iva-compras',
    label: 'IVA Compras',
    description:
      'Libro de IVA Compras para presentación AFIP. Desglose por alícuota + percepciones.',
    filterKind: 'month',
  },
  {
    id: 'iva-ventas',
    label: 'IVA Ventas',
    description:
      'Libro de IVA Ventas del mes — por operación: cliente, RFC/CUIT, desglose por alícuota y total cotización.',
    filterKind: 'month',
  },
  {
    id: 'comisiones',
    label: 'Comisiones de Venta',
    description: 'Comisiones por vendedor según ventas cobradas en el mes.',
    filterKind: 'month',
  },
  {
    id: 'ventas-efectivo',
    label: 'Ventas Efectivo Clientes',
    description:
      'Cobros en efectivo (Pesos o Dólar) del mes: clientes, facturas y montos por medio de pago.',
    filterKind: 'month',
  },
  {
    id: 'pagos-efectivo',
    label: 'Pagos Efectivo Proveedor',
    description:
      'Pagos a proveedores del mes cuya compra tiene un instrumento en efectivo. Desglose por moneda.',
    filterKind: 'month',
  },
  {
    id: 'cc-proveedores',
    label: 'Cuenta Corriente Proveedores',
    description:
      'Saldo pendiente por proveedor: compras no pagadas + NCs y retenciones aplicadas.',
    filterKind: 'date-range',
  },
  {
    id: 'cc-ventas',
    label: 'Cuenta Corriente Ventas',
    description:
      'Saldo pendiente por cliente: ventas no cobradas con detalle de cobros parciales.',
    filterKind: 'date-range',
  },
  {
    id: 'despachos',
    label: 'Despachos',
    description:
      'Despachos del mes: cliente, artículo, n° de serie, costo y vendedor.',
    filterKind: 'month',
  },
  {
    // Bespoke report — `filterKind` is unused (ignored by the branch
    // below) because the inner component owns its own toolbar (Año +
    // Caja). Listed here so it appears in the Reportes dropdown.
    id: 'cierres-de-caja',
    label: 'Cierres de Caja',
    description:
      'Cierres de Caja históricos por año y caja. Vista previa + export Excel por cierre.',
    filterKind: 'month',
  },
  {
    id: 'movimientos-historico',
    label: 'Histórico de Movimientos',
    description:
      'Todos los movimientos de caja confirmados y pendientes, con la estructura de Caja Cerrada y el tipo de cambio de los movimientos en USD.',
    filterKind: 'date-range',
    // Arranca con todo el historial (el primer movimiento es de 2025).
    desdeDefault: '01/01/2025',
  },
  {
    // MX-only (ver `availableReports`): el costo contable lo carga el
    // inventario de Italmexy y lo usa la economía Nova.
    id: 'costo-contable',
    label: 'Costo Contable por Producto',
    description:
      'Catálogo vigente con el costo contable (sin IVA) y el precio de lista de cada artículo. Incluye los que todavía no tienen costo cargado.',
    filterKind: 'none',
  },
  {
    id: 'productos-vendidos',
    label: 'Productos Vendidos por Mes',
    description:
      'Unidades vendidas de cada artículo, mes a mes. La venta cuenta en el mes de su último pago; excluye regalos y anuladas.',
    filterKind: 'date-range',
    desdeDefault: () => `01/01/${new Date().getFullYear()}`,
  },
  {
    id: 'garantias',
    label: 'Productos Entregados con Garantía',
    description:
      'Unidades entregadas con garantía por producto, de mayor a menor, según la fecha de entrega. Cada producto se abre para ver sus entregas y vencimientos.',
    filterKind: 'date-range',
    desdeDefault: () => `01/01/${new Date().getFullYear()}`,
  },
  {
    // Bespoke report, AR-only (ver `availableReports`) — era su propio módulo
    // hasta 2026-07-21. `filterKind` no se usa: trae su propio buscador,
    // filtros y pie de totales.
    id: 'facturacion',
    label: 'Facturación',
    description: 'Comprobantes electrónicos emitidos por ARCA: Nº, CAE, vto y saldo.',
    filterKind: 'month',
  },
  {
    // Bespoke report — reemplaza el modelo externo (PowerBI / planilla).
    // `filterKind` no se usa: trae su propio selector de período.
    id: 'estado-financiero',
    label: 'Estado Financiero',
    description:
      'Flujo de caja por mes agrupado por Ítem y SubÍtem del EF, con Margen Bruto de Caja y G/P Operativo.',
    filterKind: 'month',
  },
];

const PREVIEW_LIMIT = 250;

/**
 * The reports the `nova` role may open. Nova is an external partner —
 * they get their own ventas + cajas and nothing about Fornitalia's
 * compras, proveedores, comisiones or despachos. Mirrors `NOVA_REPORTS`
 * in [routes/reportes.ts](../../../server/src/routes/reportes.ts), which
 * enforces it (and additionally scopes the rows to Nova-flow ventas /
 * Nova cajas). Keep both lists in sync.
 */
const NOVA_REPORT_IDS = new Set<ReportId>([
  'iva-ventas',
  'cc-ventas',
  'ventas-efectivo',
  'cierres-de-caja',
]);

export function ReportesPage() {
  const { config: activeCountry } = useActiveCountry();
  const isNova = useProfile()?.perfil === 'nova';

  // Country- + role-scoped report list. MX has no retención régimen, so
  // Retenciones is dropped from the dropdown there; Facturación is ARCA
  // (AR electronic invoicing) so it's AR-only. Source-of-truth for the MX
  // set: mex-files/msapp-extracted/Src/Screen_Reportes.pa.yaml
  // Cbox_TipoReporte_R.Items.
  const availableReports = useMemo(
    () =>
      REPORTS.filter((r) =>
        r.id === 'retenciones'
          ? activeCountry.features.has('retenciones')
          : r.id === 'facturacion'
            ? activeCountry.code === 'AR'
            : r.id === 'costo-contable'
              ? activeCountry.code === 'MX'
              : true,
      ).filter((r) => !isNova || NOVA_REPORT_IDS.has(r.id)),
    [activeCountry, isNova],
  );
  const defaultReportId = availableReports[0]?.id ?? 'iva-compras';

  const [reportId, setReportId] = useState<ReportId>(defaultReportId);
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState<Orden<string>>(null);
  // La búsqueda que va al server espera a que el usuario deje de tipear.
  const [busquedaServer, setBusquedaServer] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBusquedaServer(busqueda.trim()), 350);
    return () => clearTimeout(t);
  }, [busqueda]);
  const alternarOrden = (key: string) =>
    setOrden((o) =>
      !o || o.key !== key ? { key, dir: 'asc' } : o.dir === 'asc' ? { key, dir: 'desc' } : null,
    );
  const [filters, setFilters] = useState<ReportFilters>(() =>
    defaultFilters(availableReports.find((r) => r.id === defaultReportId)),
  );

  // If the currently selected report is no longer in the active country's
  // list (e.g., user picked Retenciones in AR, then switched to MX),
  // bounce back to the first available.
  useEffect(() => {
    if (!availableReports.some((r) => r.id === reportId)) {
      const fallbackId = availableReports[0]?.id;
      if (fallbackId && fallbackId !== reportId) {
        setReportId(fallbackId);
        setFilters(defaultFilters(availableReports[0]));
      }
    }
  }, [availableReports, reportId]);

  const report = availableReports.find((r) => r.id === reportId) ?? availableReports[0];
  // The Cierres de Caja report has its own filter shape (Año + Caja
  // instead of date-range/month) and its own body (cierres list + per-
  // cierre preview/export). Its toolbar controls render inline next to
  // the report dropdown so everything lands on a single row; the body
  // swaps in below. State is held in `cierres` here so both the
  // toolbar and body see the same filter values.
  const isCierres = reportId === 'cierres-de-caja';
  const cierres = useCierresFilters();
  // Facturación es el segundo reporte a medida: mismo patrón que Cierres —
  // sus controles (buscador + Filtrar) se meten EN esta fila de toolbar, no
  // abajo, para que el encabezado no ocupe dos renglones. `isBespoke` apaga
  // el Excel + Filtrar genéricos de los dos.
  const isFacturacion = reportId === 'facturacion';
  // Tercer reporte a medida: mismo patrón — su selector de período va en
  // esta fila de toolbar y el cuerpo se intercambia abajo.
  const isEstadoFinanciero = reportId === 'estado-financiero';
  const isBespoke = isCierres || isFacturacion || isEstadoFinanciero;
  // El Histórico usa la tabla genérica, pero además deja reimportar el Excel
  // corregido — la misma vuelta que Cierres de Caja, sobre el libro entero
  // (pedido del cliente 2026-09-22: "sanear la base entera").
  const isHistorico = reportId === 'movimientos-historico';
  const isReadOnly = useIsReadOnly();
  const isAdminTier = useIsAdminTier();
  const puedeReimputar = !isReadOnly && isAdminTier;
  const facturacion = useFacturacionReport(isFacturacion);
  const estadoFinanciero = useEstadoFinancieroFilters(isEstadoFinanciero);

  // Country in queryKey so AR/MX previews don't share a cache slot.
  const previewQ = useQuery({
    queryKey: ['reportes', activeCountry.code, reportId, filters],
    queryFn: () => getReportPreview(reportId, filters),
    enabled: !isBespoke && filtersValid(report.filterKind, filters),
    staleTime: 30_000,
  });

  const [downloading, setDownloading] = useState(false);
  const [verSinCargos, setVerSinCargos] = useState(false);
  const [importando, setImportando] = useState(false);

  const handleChangeReport = (newId: ReportId) => {
    const newReport =
      availableReports.find((r) => r.id === newId) ?? availableReports[0];
    setReportId(newId);
    setFilters(defaultFilters(newReport));
    setBusqueda('');
    setOrden(null);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const today = fechaArchivoHoy();
      await downloadReportXlsx(reportId, filters, `${reportId}_${today}.xlsx`);
      toast.success('Reporte exportado');
    } catch (err) {
      toast.error('No se pudo exportar el reporte', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setDownloading(false);
    }
  };

  // Vista previa recortada (Histórico: 250 filas de miles) → buscar y ordenar
  // en el navegador sólo vería ese pedazo, así que lo hace el server sobre el
  // libro entero. El resto de los reportes llega completo y se filtra acá.
  const enServidor = !!previewQ.data && previewQ.data.totalRowCount > previewQ.data.rows.length;
  const tablaServer = enServidor && (busquedaServer !== '' || orden != null);
  const tablaQ = useQuery({
    queryKey: ['reportes', activeCountry.code, reportId, filters, 'tabla', busquedaServer, orden],
    queryFn: () =>
      getReportPreview(reportId, filters, { q: busquedaServer, orden: orden?.key, dir: orden?.dir }),
    enabled: tablaServer,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const datosTabla = tablaServer ? (tablaQ.data ?? previewQ.data) : previewQ.data;
  const preview = datosTabla ? adaptPreviewForDisplay(reportId, datosTabla) : undefined;

  const canDownload =
    filtersValid(report.filterKind, filters) &&
    !!previewQ.data &&
    previewQ.data.rows.length > 0 &&
    !downloading;

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Toolbar — DOM order matches desktop (Title, Excel, Report Select,
          Filter). On mobile a `basis-full` break drops Report Select +
          Filter onto a second row, leaving the title row with [Title ·
          Excel (icon only)]. */}
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <h1 className="mr-auto flex items-center gap-2 text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Reportes
        </h1>
        {!isBespoke && (
          <Button
            onClick={handleDownload}
            disabled={!canDownload}
            className="h-12 w-12 shrink-0 gap-0 bg-foreground p-0 text-background hover:bg-foreground/90 sm:w-auto sm:gap-2 sm:px-4"
            aria-label="Exportar a Excel"
            title={
              canDownload
                ? 'Exportar a Excel (.xlsx)'
                : previewQ.data?.rows.length === 0
                  ? 'Sin datos para exportar'
                  : 'Ajustá los filtros para generar el reporte'
            }
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <FileSpreadsheet className="h-4 w-4" aria-hidden />
            )}
            <span className="hidden sm:inline">Exportar Excel</span>
          </Button>
        )}
        {isHistorico && puedeReimputar && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setImportando(true)}
            className="h-12 w-12 shrink-0 gap-0 p-0 sm:w-auto sm:gap-2 sm:px-4"
            aria-label="Importar Excel"
            title="Subir el Excel con las clasificaciones corregidas"
          >
            <Upload className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Importar Excel</span>
          </Button>
        )}
        {/* Forces report select + filter onto a new row on mobile only. */}
        <div className="basis-full sm:hidden" aria-hidden />
        {/* Cierres de Caja's Año + Caja selectors render inline so the
            whole toolbar lives on one row. They sit BEFORE the report
            dropdown so the user reads left-to-right: "looking at year
            X, caja Y → Cierres de Caja report". */}
        {isCierres && (
          <CierresDeCajaToolbar
            filters={cierres}
            onChange={({ anio, caja }) => {
              cierres.setAnio(anio);
              cierres.setCaja(caja);
            }}
          />
        )}
        {isFacturacion && <FacturacionSearch state={facturacion} />}
        {!isBespoke && (
          <div className="relative min-w-[10rem] flex-1 sm:w-56 sm:flex-none">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              variant="filled"
              placeholder="Buscar"
              className="h-12 pl-9"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              aria-label="Buscar en el reporte"
            />
          </div>
        )}
        <div className="min-w-[10rem] flex-1 sm:w-64 sm:flex-none">
          <Select
            value={reportId}
            onValueChange={(v) => handleChangeReport(v as ReportId)}
          >
            <SelectTrigger className="h-12">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...availableReports]
                .sort((a, b) => a.label.localeCompare(b.label, 'es'))
                .map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        {!isBespoke && report.filterKind !== 'none' && (
          <FilterPopover
            report={report}
            filters={filters}
            onChange={setFilters}
            isFetching={previewQ.isFetching}
            vendedores={previewQ.data?.vendedores ?? []}
          />
        )}
        {/* Al extremo derecho, en el mismo lugar que el Filtrar genérico. */}
        {isFacturacion && <FacturacionFilter state={facturacion} />}
        {isEstadoFinanciero && (
          <EstadoFinancieroToolbar
            state={estadoFinanciero}
            exportar={
              estadoFinanciero.query.data
                ? () => exportEstadoFinanciero(estadoFinanciero.query.data!)
                : undefined
            }
          />
        )}
      </div>

      {isCierres ? (
        <CierresDeCajaBody filters={cierres} />
      ) : isFacturacion ? (
        <FacturacionBody state={facturacion} />
      ) : isEstadoFinanciero ? (
        <EstadoFinancieroBody state={estadoFinanciero} />
      ) : (
        <>
          {reportId === 'garantias' ? (
            <GarantiasTable
              data={previewQ.data}
              busqueda={busqueda}
              loading={previewQ.isPending && filtersValid(report.filterKind, filters)}
              error={previewQ.isError}
            />
          ) : (
          <PreviewTable
            busqueda={busqueda}
            orden={orden}
            onSort={alternarOrden}
            local={!enServidor}
            data={preview}
            loading={previewQ.isPending && filtersValid(report.filterKind, filters)}
            error={previewQ.isError}
            limit={PREVIEW_LIMIT}
          />
          )}

          <TotalsFooter
            totals={preview?.totals ?? []}
            acciones={
              preview?.cobrosMpSinCargos?.length
                ? { 'Cobros MP sin cargos': () => setVerSinCargos(true) }
                : undefined
            }
          />
          {verSinCargos && (
            <CobrosMpSinCargosDialog
              cobros={preview?.cobrosMpSinCargos ?? []}
              onClose={() => setVerSinCargos(false)}
            />
          )}
        </>
      )}

      {importando && (
        <ImportarReclasificacionDialog
          contexto="Histórico de Movimientos"
          origen="Histórico de Movimientos"
          subirArchivo={importarMovimientosHistorico}
          onClose={() => setImportando(false)}
          // Lo aplicado cambia la categoría de filas que la tabla ya tiene en
          // pantalla: sin esto el preview sigue mostrando la imputación vieja.
          onAplicado={() => void previewQ.refetch()}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter popover
// ---------------------------------------------------------------------------

function FilterPopover({
  report,
  filters,
  onChange,
  isFetching,
  vendedores,
}: {
  report: ReportDef;
  filters: ReportFilters;
  onChange: (next: ReportFilters) => void;
  isFetching: boolean;
  /** Comisiones-only — vendedor roster for the active month. */
  vendedores: string[];
}) {
  const summary = filterSummary(report.filterKind, filters);
  // Comisiones gains a per-vendedor filter (PowerApps parity — the
  // client relied on being able to scope the report to one vendedor).
  const showVendedor = report.id === 'comisiones';
  const vendedorSummary =
    showVendedor && filters.vendedor ? ` · ${filters.vendedor}` : '';
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-12 gap-2">
          <Filter className="h-4 w-4" aria-hidden />
          {summary ? `${summary}${vendedorSummary}` : 'Filtrar'}
          {isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-4">
        {report.filterKind === 'date-range' ? (
          <DateRangeFilter value={filters} onChange={onChange} />
        ) : (
          <MonthFilter value={filters} onChange={onChange} />
        )}
        {showVendedor && (
          <VendedorFilter
            value={filters}
            onChange={onChange}
            vendedores={vendedores}
          />
        )}
        <p className="mt-3 text-xs text-muted-foreground">{report.description}</p>
      </PopoverContent>
    </Popover>
  );
}

const VENDEDOR_ALL = '__all__';

function VendedorFilter({
  value,
  onChange,
  vendedores,
}: {
  value: ReportFilters;
  onChange: (next: ReportFilters) => void;
  vendedores: string[];
}) {
  return (
    <div className="mt-3 space-y-1">
      <Label>Vendedor</Label>
      <Select
        value={value.vendedor || VENDEDOR_ALL}
        onValueChange={(v) =>
          onChange({ ...value, vendedor: v === VENDEDOR_ALL ? undefined : v })
        }
        disabled={vendedores.length === 0}
      >
        <SelectTrigger aria-label="Vendedor">
          <SelectValue placeholder="Todos los vendedores" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={VENDEDOR_ALL}>Todos los vendedores</SelectItem>
          {vendedores.map((v) => (
            <SelectItem key={v} value={v}>
              {v}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function DateRangeFilter({
  value,
  onChange,
}: {
  value: ReportFilters;
  onChange: (next: ReportFilters) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="rep-desde">Desde</Label>
        <div className="relative">
          <Calendar
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id="rep-desde"
            placeholder="dd/mm/aaaa"
            className="pl-9"
            value={value.desde ?? ''}
            onChange={(e) => onChange({ ...value, desde: e.target.value })}
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="rep-hasta">Hasta</Label>
        <div className="relative">
          <Calendar
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id="rep-hasta"
            placeholder="dd/mm/aaaa"
            className="pl-9"
            value={value.hasta ?? ''}
            onChange={(e) => onChange({ ...value, hasta: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}

const MONTH_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '01', label: 'Enero' },
  { value: '02', label: 'Febrero' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Mayo' },
  { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

function MonthFilter({
  value,
  onChange,
}: {
  value: ReportFilters;
  onChange: (next: ReportFilters) => void;
}) {
  const [mm, yyyy] = (value.mes ?? '').split('/');
  const currentYear = new Date().getFullYear();
  // Past 4 years + current + next so admins can prep next-month reports
  // without scrolling for old data we don't have anyway.
  const years: string[] = [];
  for (let y = currentYear + 1; y >= currentYear - 4; y--) years.push(String(y));

  const fallbackMonth = String(new Date().getMonth() + 1).padStart(2, '0');
  const fallbackYear = String(currentYear);

  const setMonth = (m: string) =>
    onChange({ ...value, mes: `${m}/${yyyy || fallbackYear}` });
  const setYear = (y: string) =>
    onChange({ ...value, mes: `${mm || fallbackMonth}/${y}` });

  return (
    <div className="space-y-1">
      <Label>Mes / Año</Label>
      <div className="grid grid-cols-2 gap-2">
        <Select value={mm || undefined} onValueChange={setMonth}>
          <SelectTrigger aria-label="Mes">
            <SelectValue placeholder="Mes" />
          </SelectTrigger>
          <SelectContent>
            {MONTH_OPTIONS.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={yyyy || undefined} onValueChange={setYear}>
          <SelectTrigger aria-label="Año">
            <SelectValue placeholder="Año" />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={y}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function filterSummary(kind: FilterKind, value: ReportFilters): string | null {
  if (kind === 'none') return null;
  if (kind === 'month') return value.mes && value.mes.length > 0 ? value.mes : null;
  if (value.desde && value.hasta) return `${value.desde} → ${value.hasta}`;
  return null;
}

function defaultFilters(report: ReportDef | undefined): ReportFilters {
  const kind = report?.filterKind ?? 'month';
  if (kind === 'none') return {};
  const pad2 = (n: number) => String(n).padStart(2, '0');
  const d = new Date();
  if (kind === 'month') {
    return { mes: `${pad2(d.getMonth() + 1)}/${d.getFullYear()}` };
  }
  const year = d.getFullYear();
  const month = d.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const today = new Date();
  const desdeDefault =
    typeof report?.desdeDefault === 'function' ? report.desdeDefault() : report?.desdeDefault;
  return {
    desde: desdeDefault ?? `${pad2(firstOfMonth.getDate())}/${pad2(month + 1)}/${year}`,
    hasta: `${pad2(today.getDate())}/${pad2(month + 1)}/${year}`,
  };
}

function filtersValid(kind: FilterKind, value: ReportFilters): boolean {
  if (kind === 'none') return true;
  if (kind === 'month') {
    return !!value.mes && /^(0[1-9]|1[0-2])\/\d{4}$/.test(value.mes);
  }
  const dateRe = /^\d{2}\/\d{2}\/\d{4}$/;
  return (
    !!value.desde &&
    !!value.hasta &&
    dateRe.test(value.desde) &&
    dateRe.test(value.hasta)
  );
}

// ---------------------------------------------------------------------------
// Per-report display adapter
// ---------------------------------------------------------------------------

/**
 * Per-report preview adapter. The XLSX export still ships the raw rows
 * the server emits (AFIP-shaped column sets need that), but the
 * on-screen preview is for vendors eyeballing the month — so we slim
 * each report to the columns they actually read and humanize the
 * headers.
 */
function adaptPreviewForDisplay(
  reportId: ReportId,
  data: ReportPreviewResponse,
): ReportPreviewResponse {
  switch (reportId) {
    case 'iva-compras':
      return adaptIvaCompras(data);
    case 'iva-ventas':
      return adaptIvaVentas(data);
    case 'comisiones':
      return adaptComisiones(data);
    case 'ventas-efectivo':
      return adaptVentasEfectivo(data);
    case 'despachos':
      return adaptDespachos(data);
    case 'pagos-efectivo':
      return adaptPagosEfectivo(data);
    case 'cc-proveedores':
      return adaptCcProveedores(data);
    case 'cc-ventas':
      return adaptCcVentas(data);
    case 'costo-contable':
      return adaptCostoContable(data);
    case 'retenciones':
      // Already friendly — pass through.
      return data;
    default:
      return data;
  }
}

function adaptIvaCompras(data: ReportPreviewResponse): ReportPreviewResponse {
  // The server emits country-aware bracket headers ('IVA 21%' / 'IVA 10,5%'
  // in AR, 'IVA 16%' / 'IVA 8%' in MX). Discover them from the response's
  // header array rather than hard-coding — keeps the adapter country-agnostic.
  const ivaHeaders = data.header.filter((h) => /^IVA\s*\d/.test(h));
  // First IVA header in the response is "high" (per server emission order),
  // second is "low". `ivaHigh` and `ivaLow` may be undefined for a country
  // with only one bracket; safe defaults yield 0 below.
  const [ivaHighKey, ivaLowKey] = ivaHeaders;

  const header = [
    'Fecha',
    'Tipo Comprobante',
    'N° Comprobante',
    'Proveedor',
    'CUIT',
    'Total',
    ...(ivaLowKey ? [ivaLowKey] : []),
    ...(ivaHighKey ? [ivaHighKey] : []),
    'Total IVA',
  ];
  const rows = data.rows.map((row) => {
    const ivaHigh = ivaHighKey ? parseEsDecimal(String(row[ivaHighKey] ?? '')) : 0;
    const ivaLow = ivaLowKey ? parseEsDecimal(String(row[ivaLowKey] ?? '')) : 0;
    const display: Record<string, string | number> = {
      Fecha: row.Fecha ?? '',
      'Tipo Comprobante': row.Tipo ?? '',
      'N° Comprobante': row.Comprobante ?? '',
      Proveedor: row.Proveedor ?? '',
      CUIT: row.CUIT ?? '',
      Total: row.Total ?? '',
    };
    if (ivaLowKey) display[ivaLowKey] = row[ivaLowKey] ?? '';
    if (ivaHighKey) display[ivaHighKey] = row[ivaHighKey] ?? '';
    display['Total IVA'] = formatEsDecimal(ivaHigh + ivaLow);
    return display;
  });
  return { ...data, header, rows };
}

function adaptIvaVentas(data: ReportPreviewResponse): ReportPreviewResponse {
  // Mirror the iva-compras pattern: discover IVA bracket headers from
  // the server's response so the adapter stays country-agnostic.
  const ivaHeaders = data.header.filter((h) => /^IVA\s*\d/.test(h));
  const [ivaHighKey, ivaLowKey] = ivaHeaders;

  const header = [
    'Fecha',
    'ID Venta',
    'Cliente',
    'RFC',
    'Tipo de Comprobante',
    ...(ivaHighKey ? [ivaHighKey] : []),
    ...(ivaLowKey ? [ivaLowKey] : []),
    'Total IVA',
    'Total Cotización',
  ];
  const rows = data.rows.map((row) => {
    const display: Record<string, string | number> = {
      Fecha: row.Fecha ?? '',
      'ID Venta': row.Operacion ?? '',
      Cliente: row.Cliente ?? '',
      RFC: row.CUIT ?? '',
      // Backend already resolves this to "Factura A"/… or "-"; default to "-"
      // so a not-yet-restarted backend (no field) still renders cleanly.
      'Tipo de Comprobante': row['Tipo de Comprobante'] ?? '-',
    };
    if (ivaHighKey) display[ivaHighKey] = row[ivaHighKey] ?? '';
    if (ivaLowKey) display[ivaLowKey] = row[ivaLowKey] ?? '';
    display['Total IVA'] = row['Total IVA'] ?? '';
    display['Total Cotización'] = row['Total Cotización'] ?? '';
    return display;
  });
  return { ...data, header, rows };
}

function adaptComisiones(data: ReportPreviewResponse): ReportPreviewResponse {
  // El servidor sólo manda las columnas de MercadoPago en AR.
  const conMp = data.header.includes('TotalMP');
  const header = [
    'Fecha',
    'ID Venta',
    'Cliente',
    'Vendedora',
    'Monto',
    ...(conMp ? ['Costo MP', 'Intereses MP', 'Total MP', 'Neto'] : []),
    'Tasa',
    'Comisión',
  ];
  const rows = data.rows.map((row) => {
    // Cobros MercadoPago de la venta a los que Tesorería aún no les cargó
    // los cargos: la comisión de esa venta está calculada de más.
    const faltan = Number(row.CobrosMPSinCargos ?? 0);
    const deCliente = row.ComisionCliente === 'Sí';
    return {
      Fecha: row.FechaPago ?? '',
      'ID Venta': row.Operacion ?? '',
      Cliente: row.Cliente ?? '',
      Vendedora: row.Vendedor ?? '',
      Monto: row.MontoDescontado ?? '',
      'Costo MP': row.CostoMercadoPago ?? '',
      'Intereses MP': row.InteresesAbsorbidosMP ?? '',
      'Total MP': faltan > 0 ? `${row.TotalMP ?? ''} ⚠ faltan ${faltan}` : (row.TotalMP ?? ''),
      Neto: row.MontoNeto ?? '',
      Tasa: deCliente ? `${row.Tasa} (cliente)` : (row.Tasa ?? ''),
      Comisión: row.ComisionAplicada ?? '',
      // Fuera del header: sólo pinta la fila (PreviewTable).
      ...(deCliente ? { _destacada: 'cliente' } : {}),
    };
  });
  // En pantalla los totales van en UNA fila: rótulos cortos (el .xlsx
  // conserva los largos).
  const totals = data.totals.map((t) => ({ ...t, label: TOTAL_CORTO_COMISIONES[t.label] ?? t.label }));
  return { ...data, header, rows, totals };
}

const TOTAL_CORTO_COMISIONES: Record<string, string> = {
  'Total Ventas del Mes': 'Ventas',
  'Costo de Mercado Pago': 'Costo MP',
  'Intereses absorbidos MP': 'Intereses MP',
  'Base Comisionable': 'Base',
  'Comisión Aplicada': 'Comisión',
  'Comisión por cliente': 'Por cliente',
  'Cobros MP sin cargos cargados': 'Cobros MP sin cargos',
};

function adaptVentasEfectivo(data: ReportPreviewResponse): ReportPreviewResponse {
  const header = ['Fecha', 'ID Venta', 'Cliente', 'Factura Nro°', 'Monto'];
  const rows = data.rows.map((row) => ({
    Fecha: row.Fecha ?? '',
    'ID Venta': row.Operacion ?? '',
    Cliente: row.Cliente ?? '',
    'Factura Nro°': row['N° Factura'] ?? '',
    Monto: row.Monto ?? '',
  }));
  return { ...data, header, rows };
}

function adaptDespachos(data: ReportPreviewResponse): ReportPreviewResponse {
  const header = ['Fecha', 'Cliente', 'Artículo', 'N° Serie', 'Costo', 'Vendedor'];
  const rows = data.rows.map((row) => ({
    Fecha: row.Fecha ?? '',
    Cliente: row.Cliente ?? '',
    Artículo: row.Articulo ?? '',
    'N° Serie': row['N° Serie'] ?? '',
    Costo: row.Costo ?? '',
    Vendedor: row.Vendedor ?? '',
  }));
  return { ...data, header, rows };
}

/**
 * El .xlsx lleva costo y precio como números (para sumar y ordenar en Excel);
 * en pantalla van con su moneda. El costo contable está en la moneda local
 * (viene en el encabezado); el precio, en la moneda de cada artículo.
 */
function adaptCostoContable(data: ReportPreviewResponse): ReportPreviewResponse {
  const costoCol = data.header.find((h) => h.startsWith('Costo Contable'));
  const monedaCosto = currencyFromSp(/\b(ARS|MXN|USD)\b/.exec(costoCol ?? '')?.[1]);
  const PRECIO = 'Precio de Lista (sin IVA)';
  const rows = data.rows.map((row) => ({
    ...row,
    ...(costoCol ? { [costoCol]: formatMoney(row[costoCol], monedaCosto, '') } : {}),
    [PRECIO]: formatMoney(row[PRECIO], currencyFromSp(String(row['Moneda Precio'] ?? '')), ''),
  }));
  return { ...data, rows };
}

function adaptPagosEfectivo(data: ReportPreviewResponse): ReportPreviewResponse {
  const header = ['Fecha', 'ID Compra', 'Proveedor', 'Comprobante', 'Observaciones', 'Importe'];
  const rows = data.rows.map((row) => ({
    Fecha: row.Fecha ?? '',
    'ID Compra': row.Compra ?? '',
    Proveedor: row.Proveedor ?? '',
    Comprobante: row.Comprobante ?? '',
    Observaciones: row.Observaciones ?? '',
    Importe: row.Importe ?? '',
  }));
  return { ...data, header, rows };
}

function adaptCcProveedores(data: ReportPreviewResponse): ReportPreviewResponse {
  const header = [
    'Proveedor',
    'CUIT',
    'Condición IVA',
    'Comprobante',
    'N° Comprobante',
    'Moneda',
    'Fecha',
    'Vencimiento',
    'Total',
    'Pagado',
    'Retenido',
    'NC Pendiente',
    'Restante',
    'Estado',
  ];
  const rows = data.rows.map((row) => ({
    Proveedor: row.Proveedor ?? '',
    CUIT: row.CUIT ?? '',
    'Condición IVA': row.CondicionIVA ?? '',
    Comprobante: row.Comprobante ?? '',
    'N° Comprobante': row.NumeroComprobante ?? '',
    Moneda: row.Moneda ?? '',
    Fecha: row.Fecha ?? '',
    Vencimiento: row.Vencimiento ?? '',
    Total: row.Total ?? '',
    Pagado: row.Pagado ?? '',
    Retenido: row.Retenido ?? '',
    'NC Pendiente': row.NC_Pendiente ?? '',
    Restante: row.Restante ?? '',
    Estado: row.Estado ?? '',
  }));
  return { ...data, header, rows };
}

function adaptCcVentas(data: ReportPreviewResponse): ReportPreviewResponse {
  const header = [
    'Cliente',
    'CUIT',
    'ID Venta',
    'Fecha',
    'Fecha Pago',
    'Moneda',
    'Total',
    'Cobrado',
    'Restante',
    'Estado',
    'Vendedor',
  ];
  const rows = data.rows.map((row) => ({
    Cliente: row.Cliente ?? '',
    CUIT: row.CUIT ?? '',
    'ID Venta': row.Operacion ?? '',
    Fecha: row.Fecha ?? '',
    'Fecha Pago': row.FechaPago ?? '',
    Moneda: row.Moneda ?? '',
    Total: row.Total ?? '',
    Cobrado: row.Cobrado ?? '',
    Restante: row.Restante ?? '',
    Estado: row.Status ?? '',
    Vendedor: row.Vendedor ?? '',
  }));
  return { ...data, header, rows };
}

function parseEsDecimal(s: string): number {
  if (!s) return 0;
  const normalized = s.includes(',')
    ? s.replace(/\./g, '').replace(',', '.')
    : s;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

function formatEsDecimal(n: number): string {
  if (!Number.isFinite(n)) return '0,00';
  const fixed = n.toFixed(2);
  const [whole, frac] = fixed.split('.');
  const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${withThousands},${frac}`;
}

// ---------------------------------------------------------------------------
// Preview table
// ---------------------------------------------------------------------------

type FilaReporte = ReportPreviewResponse['rows'][number];
const valorCelda = (row: FilaReporte, header: string) => row[header];

function PreviewTable({
  data,
  busqueda,
  orden,
  onSort,
  local,
  loading,
  error,
  limit,
}: {
  data: ReportPreviewResponse | undefined;
  /** Filtra sobre todas las columnas (importes incluidos). No toca el Excel ni los totales. */
  busqueda: string;
  orden: Orden<string>;
  onSort: (header: string) => void;
  /** true: las filas llegaron completas y se filtran/ordenan acá. false: ya vienen del server. */
  local: boolean;
  loading: boolean;
  error: boolean;
  limit: number;
}) {
  const filtradas = useMemo(() => {
    const rows = data?.rows ?? [];
    if (!local || !data) return rows;
    const b = prepararBusqueda(busqueda);
    const coinciden = b ? rows.filter((row) => coincideBusqueda(data.header.map((h) => row[h]), b)) : rows;
    return ordenarFilas(coinciden, orden, valorCelda);
  }, [data, busqueda, orden, local]);
  const visibleRows = useMemo(() => filtradas.slice(0, limit), [filtradas, limit]);
  // Local: lo filtrado. Server: el total que coincide (las filas llegan recortadas).
  const totalFilas = local ? filtradas.length : (data?.totalRowCount ?? 0);

  if (error) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border bg-card">
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" aria-hidden />
          No se pudo generar el reporte.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-0 flex-1 space-y-2 rounded-2xl border bg-card p-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-5 w-full" />
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border bg-card">
        <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
          <FileBarChart2 className="h-8 w-8" aria-hidden />
          Ajustá los filtros para generar el reporte.
        </div>
      </div>
    );
  }

  if (data.rows.length === 0 && !busqueda.trim()) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border bg-card">
        <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
          <FileBarChart2 className="h-8 w-8" aria-hidden />
          Sin resultados para los filtros aplicados.
        </div>
      </div>
    );
  }

  if (filtradas.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border bg-card">
        <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
          <Search className="h-8 w-8" aria-hidden />
          Sin coincidencias para la búsqueda.
        </div>
      </div>
    );
  }

  const truncated = totalFilas > limit;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      {/* Desktop / tablet table */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 border-b bg-card text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {data.header.map((h) => (
                <SortableTh
                  key={h}
                  label={h}
                  sortKey={h}
                  orden={orden}
                  onSort={onSort}
                  className={cn(
                    'whitespace-nowrap px-4 py-3 font-medium',
                    isNumericColumn(h) && 'text-right',
                  )}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, i) => (
              <tr
                key={i}
                className={cn(
                  'border-b border-border/60 transition-colors last:border-b-0',
                  // Subtle zebra so the eye can lock onto a row across
                  // wide reports without darting back to the header.
                  i % 2 === 1 && 'bg-muted/10',
                  row._destacada && 'bg-amber-100/70 dark:bg-amber-500/15',
                  'hover:bg-primary/5',
                )}
              >
                {data.header.map((h) => {
                  const value = row[h];
                  const isNumeric = isNumericColumn(h);
                  const display = value == null || value === '' ? '—' : value;
                  const fullText = value == null ? '' : String(value);
                  // Per-column max-width: long-text columns (Concepto,
                  // Descripción, Razón Social) clamp tight so they
                  // can't push numeric columns out of the viewport;
                  // short labels (Tipo, CUIT) stay loose. Excel export
                  // is unaffected — server ships the raw row.
                  const maxW = textColumnMaxWidth(h);
                  return (
                    <td
                      key={h}
                      className={cn(
                        'whitespace-nowrap px-4 py-3 align-middle',
                        isNumeric &&
                          'text-right tabular-nums text-foreground',
                      )}
                    >
                      {isNumeric ? (
                        display
                      ) : (
                        <span
                          className={cn('block truncate', maxW)}
                          title={fullText || undefined}
                        >
                          {display}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards — stacks columns as label/value pairs. Reports are
       *  intrinsically wide; the mobile view is a "browse" affordance
       *  while the real consumption is the Excel export.
       */}
      <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto md:hidden">
        {visibleRows.map((row, i) => (
          <li key={i} className="px-4 py-3 text-sm">
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
              {data.header.map((h) => {
                const value = row[h];
                const isNumeric = isNumericColumn(h);
                const display = value == null || value === '' ? '—' : value;
                return (
                  <div key={h} className="contents">
                    <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {h}
                    </dt>
                    <dd
                      className={cn(
                        'min-w-0 break-words',
                        isNumeric && 'text-foreground tabular-nums',
                      )}
                    >
                      {display}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </li>
        ))}
      </ul>

      {truncated && (
        <div className="flex shrink-0 items-center justify-end border-t bg-amber-50 px-4 py-2 text-xs text-amber-800">
          {busqueda.trim() ? (
            <>
              Se muestran las primeras {limit} de {totalFilas.toLocaleString('es-AR')} coincidencias —
              afiná la búsqueda para ver el resto.
            </>
          ) : (
            <>
              Vista previa limitada a {limit} filas — exportá a Excel para el reporte completo (
              {data.totalRowCount.toLocaleString('es-AR')} filas).
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Per-column truncation cap for non-numeric text columns. Long-text
 * columns get a tight max-width so they can't dominate the table; the
 * full value still surfaces on hover via the row's `title` attribute.
 */
function textColumnMaxWidth(header: string): string {
  const h = header.toLowerCase();
  if (
    /concepto|descripci[oó]n|raz[oó]n.*social|cliente|proveedor|art[ií]culo|observ/i.test(
      h,
    )
  ) {
    return 'max-w-[220px]';
  }
  if (/medio|estado|tipo|moneda/i.test(h)) {
    return 'max-w-[140px]';
  }
  return 'max-w-[260px]';
}

// ---------------------------------------------------------------------------
// Totals footer
// ---------------------------------------------------------------------------

function TotalsFooter({
  totals,
  acciones,
}: {
  totals: ReportTotal[];
  /** Rótulo → acción: agrega un "Ver detalle" dentro de ese chip. */
  acciones?: Record<string, () => void>;
}) {
  if (totals.length === 0) return null;
  // En el celular, grilla de 2 columnas con el rótulo arriba: apilados uno por
  // renglón los totales se comían media pantalla.
  return (
    <div className="grid shrink-0 grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-end sm:gap-3">
      {totals.map((t) => (
        <div
          key={t.label}
          className="flex min-w-0 flex-col gap-0.5 rounded-lg border bg-card px-3 py-1.5 text-xs sm:inline-flex sm:flex-row sm:items-center sm:gap-3 sm:px-4 sm:py-2 sm:text-sm"
        >
          <span className="text-muted-foreground">{t.label}:</span>
          <span className="font-semibold tabular-nums text-foreground">
            {t.value}
          </span>
          {acciones?.[t.label] && (
            <button
              type="button"
              onClick={acciones[t.label]}
              className="self-start text-xs font-medium text-primary underline-offset-2 hover:underline sm:self-auto"
            >
              Ver detalle
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

type CobroMpSinCargos = NonNullable<ReportPreviewResponse['cobrosMpSinCargos']>[number];

/** `yyyy-mm-dd` → `dd/mm/yyyy`. */
const fechaAr = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');

/**
 * Cobros MercadoPago de las ventas del reporte a los que Tesorería todavía no
 * les cargó los cargos: hasta que se carguen, la comisión de esa venta sale
 * calculada sobre el monto entero.
 */
function CobrosMpSinCargosDialog({
  cobros,
  onClose,
}: {
  cobros: CobroMpSinCargos[];
  onClose: () => void;
}) {
  const total = cobros.reduce((s, c) => s + c.monto, 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-[95vw] flex-col lg:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Cobros MercadoPago sin cargos cargados</DialogTitle>
          <DialogDescription>
            {cobros.length} {cobros.length === 1 ? 'cobro' : 'cobros'} ·{' '}
            {formatMoney(total, 'ARS')}. Cargalos desde Tesorería, caja MercadoPago, con el botón
            de cargos de cada cobro. Hasta entonces la comisión de esas ventas se calcula sin
            descontarlos.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="min-h-0 flex-1 overflow-y-auto p-0">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Fecha</th>
                <th className="px-4 py-2 font-medium">Movimiento</th>
                <th className="px-4 py-2 font-medium">ID Venta</th>
                <th className="hidden px-4 py-2 font-medium sm:table-cell">Cliente</th>
                <th className="px-4 py-2 font-medium">Vendedora</th>
                <th className="px-4 py-2 text-right font-medium">Monto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {cobros.map((c) => (
                <tr key={c.movimiento}>
                  <td className="whitespace-nowrap px-4 py-2">{fechaAr(c.fecha)}</td>
                  <td className="px-4 py-2 tabular-nums">{c.movimiento}</td>
                  <td className="px-4 py-2 tabular-nums">{c.venta}</td>
                  <td className="hidden max-w-[220px] truncate px-4 py-2 sm:table-cell" title={c.cliente}>
                    {c.cliente}
                  </td>
                  <td className="px-4 py-2">{c.vendedora}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                    {formatMoney(c.monto, 'ARS')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/** Columns with currency/quantity labels render right-aligned + monospaced. */
function isNumericColumn(h: string): boolean {
  // Columnas por mes ('06/2026') del reporte de Productos Vendidos.
  if (/^(0[1-9]|1[0-2])\/\d{4}$/.test(h)) return true;
  return /total|monto|importe|neto|iva|perc|cobrado|pagado|restante|retenido|porcentaje|cantidad|nc[_\s]+pendiente|comisi[oó]n|umbral|tasa|descontado|imp\.|costo|intereses/i.test(
    h,
  );
}
